import React from 'react'
import PropTypes from 'prop-types'
import ClassDrawerHeaderLayout, {
  ContentWrapper,
  TitleWrapper,
} from '../Layout'
import {
  ClassInfoText,
  ClassTitle,
  LessonsComplete,
  ProgressContainer,
  ProgressSection,
  StyledProgressBar,
} from './component.styles'
import { BHU_TITLE, UNIT_1_4_TITLE } from './constants'

const toPercentComplete = (completed, total) =>
  total > 0 ? Math.floor((completed / total) * 100) : 0

/**
 * Reports context header for class drawer.
 * Displays class information with Units 1-4 (or optional alternate primary title) and BHU progress sections.
 */
const ReportsClassHeader = ({
  bhuLessons,
  classTitle,
  fixedTop,
  fixedView,
  gradeName,
  hasBHU,
  lessonCount,
  lessonsCompleted,
  primaryProgressSectionTitle = UNIT_1_4_TITLE,
  teacherFirstName,
  teacherLastName,
}) => {
  const bhuLessonsCount = bhuLessons?.length ?? 0
  const bhuTotalLessons = hasBHU ? bhuLessonsCount : 0
  const bhuLessonsCompleted =
    bhuLessons?.filter(lesson => lesson.completedDate !== null).length ?? 0
  const unit1Through4LessonCount = Math.max(0, lessonCount - bhuTotalLessons)
  const unit1Through4LessonComplete = Math.max(
    0,
    lessonsCompleted - bhuLessonsCompleted,
  )
  const isRbhuOnlyView = hasBHU && unit1Through4LessonCount === 0
  const unit1Through4LessonPercentComplete = toPercentComplete(
    unit1Through4LessonComplete,
    unit1Through4LessonCount,
  )
  const bhuPercentComplete = toPercentComplete(
    bhuLessonsCompleted,
    bhuTotalLessons,
  )
  const bhuLessonsCompleteText = `${bhuLessonsCompleted} of ${bhuTotalLessons} lessons marked done`

  const classHeaderTitle = `Class: ${classTitle}`
  const learnerGradeText = `${teacherFirstName} ${teacherLastName} • ${gradeName}`
  const fixedViewLearnerGradeText = `Class: ${classTitle} • ${learnerGradeText}`
  const lessonsCompleteText = `${unit1Through4LessonComplete} of ${unit1Through4LessonCount} lessons marked done`
  const classInfoText = fixedView ? fixedViewLearnerGradeText : learnerGradeText

  return (
    <ClassDrawerHeaderLayout fixedTop={fixedTop} fixedView={fixedView}>
      <ContentWrapper>
        <TitleWrapper>
          {!fixedView && (
            <ClassTitle dataTestId="class-title">{classHeaderTitle}</ClassTitle>
          )}
          <ClassInfoText data-testid="class-info-text">
            {classInfoText}
          </ClassInfoText>
        </TitleWrapper>
      </ContentWrapper>
      <ProgressContainer>
        {!isRbhuOnlyView && (
          <ProgressSection>
            {primaryProgressSectionTitle}
            {!fixedView && (
              <StyledProgressBar
                dataTestId="class-progress-bar"
                percentComplete={unit1Through4LessonPercentComplete}
              />
            )}
            <LessonsComplete data-testid="lessons-complete">
              {lessonsCompleteText}
            </LessonsComplete>
          </ProgressSection>
        )}
        {hasBHU && (
          <ProgressSection>
            {BHU_TITLE}
            {!fixedView && (
              <StyledProgressBar
                dataTestId="class-progress-bar"
                percentComplete={bhuPercentComplete}
              />
            )}
            <LessonsComplete data-testid="lessons-complete">
              {bhuLessonsCompleteText}
            </LessonsComplete>
          </ProgressSection>
        )}
      </ProgressContainer>
    </ClassDrawerHeaderLayout>
  )
}

ReportsClassHeader.propTypes = {
  bhuLessons: PropTypes.array,
  classTitle: PropTypes.string,
  fixedTop: PropTypes.bool,
  fixedView: PropTypes.bool,
  gradeName: PropTypes.string,
  hasBHU: PropTypes.bool,
  lessonCount: PropTypes.number,
  lessonsCompleted: PropTypes.number,
  primaryProgressSectionTitle: PropTypes.string,
  teacherFirstName: PropTypes.string,
  teacherLastName: PropTypes.string,
}

export default ReportsClassHeader
